import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import LoadingSpinner from "../components/LoadingSpinner";
import ReportMaintenanceButton from "../components/ReportMaintenanceButton";
import {
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../components/ResponsiveDataTable";
import StateBadge from "../components/StateBadge";
import { useAuth } from "../contexts/AuthContext";
import { useBoxes } from "../hooks/useBoxes";
import { STACKED_TW, STATE_TW } from "../lib/colors";
import { type BoxBucket, bucketOf } from "../lib/stateCounts";
import { fmtCustomerWc, fmtDuration, fmtUtc } from "../lib/format";
import type { Box, State } from "../types/boxes";

const STATE_ORDER: Record<State, number> = {
  POWERED_DOWN: 0,
  DOWN: 1,
  SCANNER_ONLY: 2,
  CAMERA_ONLY: 3,
  UNKNOWN: 4,
  UP: 5,
  UNTOUCHED: 6,
};

/**
 * Column layout for both the desktop table and the mobile card. SCU
 * doubles as the mobile-card header (rendered separately by
 * `mobileHeader`), so it's `hideOnMobile` here to avoid duplication.
 */
/**
 * Columns for the Boxes table. Platform users get the full operator view
 * (cause/primary/signal dots/ticket + a link to the signal-detail page).
 * Customer/vendor users get a limited view — identity, smartbox number,
 * state, and timing — with the action link going to the inventory page
 * (signal detail is platform-only).
 */
const makeBoxesColumns = (
  operatorView: boolean,
  canNewScan: boolean,
): ResponsiveColumn<Box>[] => {
  const cols: ResponsiveColumn<Box>[] = [
    {
      key: "scu",
      header: "SCU",
      hideOnMobile: true,
      cell: (b) => <code className="font-mono text-xs">{b.scu_name}</code>,
    },
    {
      key: "customer",
      header: operatorView ? "Customer / WC" : "Smartbox / Customer",
      cell: (b) => (
        <span className="text-xs">
          {b.smartbox_code ? (
            <span className="font-semibold">{b.smartbox_code} · </span>
          ) : null}
          {fmtCustomerWc(b.customer, b.work_center)}
        </span>
      ),
    },
    {
      key: "state",
      header: "State",
      hideOnMobile: true, // shown in the mobile card header
      cell: (b) => (
        <div className="flex items-center gap-1 flex-wrap">
          <StateBadge state={b.state} />
          {b.is_stacked && (
            <span
              className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-secondary-200 text-secondary-800 border border-secondary-400"
              title={
                b.stacked_set_by
                  ? `Stacked by ${b.stacked_set_by} (MTTR override)`
                  : "Stacked at source workcenter"
              }
            >
              STACKED
            </span>
          )}
        </div>
      ),
    },
  ];

  cols.push(
    {
      key: "since",
      header: "Since",
      className: "text-xs",
      cell: (b) => fmtUtc(b.since_utc),
    },
    {
      key: "elapsed",
      header: "Elapsed",
      cell: (b) => fmtDuration(b.elapsed_seconds),
    },
  );

  if (operatorView) {
    cols.push({
      key: "ticket",
      header: "Ticket",
      className: "text-xs",
      cell: (b) =>
        b.open_ticket_id ? (
          <span className="text-primary-700">
            {b.open_ticket_origin} #{b.open_ticket_id}
          </span>
        ) : (
          "-"
        ),
    });
  }

  cols.push({
    key: "actions",
    header: "",
    mobileLabel: "Actions",
    cell: (b) => (
      <div className="flex items-center gap-3">
        <ReportMaintenanceButton scu={b.scu_name} label={b.smartbox_code ?? b.scu_name} />
        <Link
          to={
            operatorView
              ? `/signal/${encodeURIComponent(b.scu_name)}`
              : `/inventory/${encodeURIComponent(b.scu_name)}`
          }
          className="text-primary-600 text-xs hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {operatorView ? "signal →" : "inventory →"}
        </Link>
        <Link
          to={`/photos/${encodeURIComponent(b.scu_name)}`}
          className="text-primary-600 text-xs hover:underline"
          onClick={(e) => e.stopPropagation()}
          title="Recent person-episodes + photos from this box"
        >
          photos →
        </Link>
        {canNewScan && (
          <Link
            to={`/scan/new?scu=${encodeURIComponent(b.scu_name)}`}
            className="text-primary-600 text-xs hover:underline"
            onClick={(e) => e.stopPropagation()}
            title="Scan this box now and list the tags that just arrived"
          >
            new scan →
          </Link>
        )}
      </div>
    ),
  });

  return cols;
};

export const BoxesPage: React.FC = () => {
  const { snapshot, loading, error } = useBoxes();
  const { can } = useAuth();
  // Operator columns are platform-only — via the operator.triage capability
  // (held only by platform roles), so it's capability-driven yet stays
  // limited to platform users.
  const operatorView = can("operator.triage");
  const canNewScan = can("reader.vendor_scan");
  const columns = useMemo(
    () => makeBoxesColumns(operatorView, canNewScan),
    [operatorView, canNewScan],
  );
  // A BUCKET, not a state — "STACKED" is a sibling of the states here, so the
  // chip you click and the rows you get back are the same set. Filtering on
  // `b.state` was the bug: the Down chip excluded stacked boxes (the shared
  // count helper pulls them out) while the Down LIST still included them.
  const [stateFilter, setStateFilter] = useState<BoxBucket | "ALL">("ALL");
  const [customerFilter, setCustomerFilter] = useState<string>("");

  const customers = useMemo(() => {
    const set = new Set<string>();
    snapshot?.boxes.forEach((b) => {
      if (b.customer) set.add(b.customer);
    });
    return ["", ...Array.from(set).sort()];
  }, [snapshot]);

  const filtered = useMemo<Box[]>(() => {
    if (!snapshot) return [];
    return [...snapshot.boxes]
      .filter((b) => stateFilter === "ALL" || bucketOf(b) === stateFilter)
      .filter(
        (b) =>
          !customerFilter ||
          (b.customer ?? "").toLowerCase() === customerFilter.toLowerCase(),
      )
      .sort(
        (a, b) =>
          STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
          a.scu_name.localeCompare(b.scu_name),
      );
  }, [snapshot, stateFilter, customerFilter]);

  return (
    <div className="p-4 md:p-6">
      <header className="mb-4">
        <h1 className="text-xl font-bold">SmartBoxes</h1>
        {/* Counts + state-count chips. Horizontal-scroll container on
            mobile so the strip never wraps awkwardly; chip touch
            targets bumped to 44px min-height. */}
        {/* State-count chips — same visual language as the Map page
            (px-2 py-0.5 rounded-full + STATE_TW per-state color). */}
        <div className="text-xs text-secondary-500 mt-1 flex flex-wrap items-center gap-1.5 min-w-0">
          <span className="shrink-0">
            {snapshot
              ? `${filtered.length} of ${snapshot.total_boxes}`
              : "loading…"}
          </span>
          {snapshot && (
            <>
              {(
                [
                  "UP",
                  "SCANNER_ONLY",
                  "CAMERA_ONLY",
                  "UNKNOWN",
                  "DOWN",
                  "POWERED_DOWN",
                  "UNTOUCHED",
                ] as State[]
              ).map((s) => {
                const count = snapshot.state_counts?.[s] ?? 0;
                if (count === 0) return null;
                const active = stateFilter === s;
                const label =
                  s === "SCANNER_ONLY"
                    ? "Scanner-only"
                    : s === "CAMERA_ONLY"
                      ? "Camera-only"
                      : s === "POWERED_DOWN"
                        ? "Powered-down"
                        : s.charAt(0) + s.slice(1).toLowerCase();
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() =>
                      setStateFilter((prev) => (prev === s ? "ALL" : s))
                    }
                    className={
                      "shrink-0 px-2 py-0.5 rounded-full border transition-colors " +
                      (active
                        ? "ring-2 ring-primary-400 " + STATE_TW[s]
                        : STATE_TW[s] + " hover:brightness-95")
                    }
                    title={`Click to ${active ? "clear" : "show only"} ${s} boxes`}
                  >
                    {label} <strong>{count}</strong>
                  </button>
                );
              })}
              {snapshot.stacked_count > 0 && (
                /* Clickable, like every other chip. It used to be a plain
                   <span>, so the one bucket you could not list was the one
                   holding boxes deliberately excluded from the others. */
                <button
                  type="button"
                  onClick={() =>
                    setStateFilter((prev) => (prev === "STACKED" ? "ALL" : "STACKED"))
                  }
                  className={
                    "shrink-0 px-2 py-0.5 rounded-full border transition-colors "
                    + (stateFilter === "STACKED"
                      ? "ring-2 ring-primary-400 " + STACKED_TW
                      : STACKED_TW + " hover:brightness-95")
                  }
                  title={
                    `Click to ${stateFilter === "STACKED" ? "clear" : "show only"} `
                    + "STACKED boxes — intentionally offline (stacked / in storage), "
                    + "so they are not counted in any state and are not triaged as down"
                  }
                >
                  Stacked <strong>{snapshot.stacked_count}</strong>
                </button>
              )}
            </>
          )}
        </div>
      </header>

      {/* Filter row stacks on mobile, inline on md:+. Bigger touch
          targets via min-h-[44px] on the selects. */}
      <div className="flex flex-col gap-2 md:flex-row md:gap-3 mb-4">
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as BoxBucket | "ALL")}
          className="px-3 py-2 min-h-[44px] border border-secondary-300 rounded-md text-sm bg-white"
        >
          <option value="ALL">All states</option>
          <option value="STACKED">Stacked</option>
          <option value="UP">UP</option>
          <option value="SCANNER_ONLY">SCANNER_ONLY</option>
          <option value="CAMERA_ONLY">CAMERA_ONLY</option>
          <option value="UNKNOWN">UNKNOWN</option>
          <option value="DOWN">DOWN</option>
          <option value="POWERED_DOWN">POWERED_DOWN</option>
          <option value="UNTOUCHED">UNTOUCHED</option>
        </select>
        <select
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
          className="px-3 py-2 min-h-[44px] border border-secondary-300 rounded-md text-sm bg-white"
        >
          {customers.map((c) => (
            <option key={c || "(any)"} value={c}>
              {c || "All customers"}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error.message}
        </div>
      )}

      {loading && !snapshot ? (
        <LoadingSpinner label="loading boxes" />
      ) : (
        <ResponsiveDataTable
          columns={columns}
          rows={filtered}
          getRowKey={(b) => b.scu_name}
          mobileHeader={(b) => (
            <div className="flex items-center justify-between gap-2">
              <code className="font-mono text-xs font-semibold">
                {b.scu_name}
              </code>
              <div className="flex items-center gap-1">
                {b.is_stacked && (
                  <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-secondary-200 text-secondary-800 border border-secondary-400">
                    STACKED
                  </span>
                )}
                <StateBadge state={b.state} />
              </div>
            </div>
          )}
        />
      )}
    </div>
  );
};

export default BoxesPage;
